import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface OrganizerReview {
  id: string;
  rating: number;
  comment: string | null;
  created_at: string;
  user_id: string;
  event_id: string | null;
  userName: string;
  userAvatar: string | null;
}

export function useOrganizerReviews(organizerId: string | null) {
  const [reviews, setReviews] = useState<OrganizerReview[]>([]);
  const [averageRating, setAverageRating] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchReviews = useCallback(async () => {
    if (!organizerId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('organizer_reviews')
        .select('id, rating, comment, created_at, user_id, event_id')
        .eq('organizer_id', organizerId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      if (!data || data.length === 0) {
        setReviews([]);
        setAverageRating(0);
        setLoading(false);
        return;
      }

      const userIds = [...new Set(data.map(r => r.user_id))];

      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, full_name, avatar_url')
        .in('user_id', userIds);

      const profileMap = new Map(
        (profiles || []).map(p => [p.user_id, p])
      );

      const mappedReviews: OrganizerReview[] = data.map(r => {
        const profile = profileMap.get(r.user_id);
        return {
          id: r.id,
          rating: r.rating,
          comment: r.comment,
          created_at: r.created_at,
          user_id: r.user_id,
          event_id: r.event_id,
          userName: profile?.full_name || 'Usuario',
          userAvatar: profile?.avatar_url || null,
        };
      });

      const avg = mappedReviews.reduce((sum, r) => sum + r.rating, 0) / mappedReviews.length;
      setAverageRating(Math.round(avg * 10) / 10);
      setReviews(mappedReviews);
    } catch (err) {
      console.error('Error fetching organizer reviews:', err);
    } finally {
      setLoading(false);
    }
  }, [organizerId]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  useEffect(() => {
    const handler = () => fetchReviews();
    window.addEventListener('organizer-review-submitted', handler);
    return () => window.removeEventListener('organizer-review-submitted', handler);
  }, [fetchReviews]);

  return { reviews, averageRating, loading, totalReviews: reviews.length };
}
